import { defineStore } from "pinia";
import { ref } from "vue";
import { API } from "@/queries/index";
import { AxiosError } from "axios";
import { LoginPayload, LoginResponseInterface } from "@/types/auth";
import { APIResponse, APIResponseError } from "@/types/api";
import { useLocalStorage } from '@vueuse/core';
import { handleResponse, handleResponseError } from "@/utils/api";
import { db } from '@/database/db';

export const useAuthStore = defineStore("authStore", () => {
  const authResponse = ref<LoginResponseInterface>();
  const token = useLocalStorage('token', '');
  const role = useLocalStorage('role', '');

  function initAuthResponse(data: LoginResponseInterface) {
    authResponse.value = data;
  }

  function addAuthResponse(auth: any) {
    authResponse.value = auth;
  }

  function saveToken(newToken: any) {
    token.value = newToken.data.token;
  }

  function clearToken() {
    token.value = '';
  }

  function saveRole(newRole: any) {
    role.value = newRole.data.role;
  }

  function clearRole() {
    role.value = '';
  }

  async function saveLoginInfo(newLoginInfo: any) {
    await db.storage.put({id:'login-info', data: newLoginInfo.data.assigned});
  }

  async function clearLoginInfo() {
    await db.storage.delete('login-info');
  }

  async function saveAccessPermissionInfo(newAccessPermissionInfo: any) {
    await db.permission.put({id:'access-permission-info', data: newAccessPermissionInfo.data});
  }

  async function clearAccessPermissionInfo() {
    await db.permission.delete('access-permission-info');
  }

  async function dispatchInitialize() : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.auth.doInitialize();
      if (status === 200 ) {
        saveLoginInfo(data);
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }

    return handleResponse(200, null);
  }

  async function dispatchLogout() : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.auth.doLogout();
      if (status === 200 ) {
        clearToken();
        clearRole();
        clearLoginInfo();
        clearAccessPermissionInfo();
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
    return handleResponse(200, null);
  }

  async function dispatchLogin(payload: LoginPayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.auth.doLogin(payload);
      if (status === 200 ) {
        addAuthResponse(data);
        saveToken(data);
        saveRole(data);
        dispatchInitialize();
        dispatchGetAccessPermission();
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
    return handleResponse(200, null);
  }

  async function dispatchGetAccessPermission() : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.auth.doGetAccessPermission();
      if (status === 200 ) {
        saveAccessPermissionInfo(data);
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }

    return handleResponse(200, null);
  }

  return {
    authResponse,
    initAuthResponse,
    addAuthResponse,
    dispatchLogin,
    dispatchInitialize,
    dispatchLogout,
    dispatchGetAccessPermission,
  };
});